// English mirror of tables.js — identical structure, translated content.

export const THEORIES = [
  { key: 'trad', name: 'Traditional view', sub: 'The present-day island' },
  { key: 'lefkada', name: 'Lefkada', sub: 'W. Dörpfeld, 1900' },
  { key: 'paliki', name: 'Paliki (Cephalonia)', sub: 'Bittlestone · Diggle · Underhill, 2005–2026' },
];

export const SCORE_META = {
  strong: { label: 'Strong match', cls: 'bg-aegean text-paper' },
  medium: { label: 'Moderate / marginal', cls: 'bg-[#C9A227]/15 text-[#7A6116] border border-[#C9A227]/40' },
  weak: { label: 'Weak / contradicted', cls: 'bg-terra/10 text-terra border border-terra/40' },
  na: { label: 'Not addressed', cls: 'bg-muted text-muted-foreground border border-line' },
};

export const CRITERIA = [
  {
    criterion: '"Zophos" = north',
    source: 'Od. 9.26',
    question: 'Is the candidate location the northernmost of the group?',
    cells: {
      trad: { score: 'weak', note: 'Satisfied only marginally — essentially at the same latitude as Cephalonia\u2019s northern tip (38.47° N).' },
      lefkada: { score: 'strong', note: 'Dramatically the northernmost: 38.50°–38.83° N, almost half a degree beyond Ithaca.' },
      paliki: { score: 'na', note: 'Not explicitly used as an argument by the theory.' },
    },
  },
  {
    criterion: '"Chthamalē" (low-lying)',
    source: 'Od. 9.25',
    question: 'Does the relief/position fit the epithet "low" or "close to the mainland"?',
    cells: {
      trad: { score: 'weak', note: 'A fairly mountainous island — the highest peak of present-day Ithaca stands at 800 m. And even if Strabo\u2019s reading (“lying close to the mainland”) is adopted, it does not meet the criterion.' },
      lefkada: { score: 'strong', note: 'Under the reading "lying close to the mainland" (Strabo): a documented former peninsula of Acarnania.' },
      paliki: { score: 'medium', note: 'A peninsula of Cephalonia — compatible with Strabo\u2019s reading, though not developed explicitly.' },
    },
  },
  {
    criterion: '"en dēmō(i) Ithakēs"',
    source: 'Od. 13.96',
    question: 'Is Ithaca part of a larger entity rather than a self-contained island?',
    cells: {
      trad: { score: 'weak', note: 'A complete, self-contained island — the phrase is structurally contradicted, not merely silent.' },
      lefkada: { score: 'strong', note: 'Part of the mainland region of Acarnania — the same argument benefits Lefkada equally (or more).' },
      paliki: { score: 'strong', note: 'The core of the Diggle–Underhill philological argument (2026): Ithaca as a "dēmos" of Cephalonia.' },
    },
  },
  {
    criterion: 'Avoidance of "nēsos"',
    source: 'the entire corpus',
    question: 'Homer never explicitly calls Ithaca a "nēso" (island) — which theory explains this?',
    cells: {
      trad: { score: 'weak', note: 'Present-day Ithaca is definitively an island — the avoidance remains unexplained.' },
      lefkada: { score: 'strong', note: 'Solves the problem radically: a former peninsula — it never needed to be an island.' },
      paliki: { score: 'medium', note: 'Strong but applied selectively — it is not explained why it does not apply equally to Lefkada.' },
    },
  },
  {
    criterion: 'Mount Neritus',
    source: 'Il. 2.632 · Od. 9.21',
    question: 'Does the candidate have an emblematic, forested mountain matching "Nēriton einosiphyllon"?',
    cells: {
      trad: { score: 'weak', note: 'No mountain bearing the name “Nēriton” exists on present-day Ithaca — its highest peak (800 m) does not carry the Homeric name.' },
      lefkada: { score: 'medium', note: 'A possible etymological link Nēriton = Nērikos (acropolis of Lefkada) — indirect, without continuous usage.' },
      paliki: { score: 'na', note: 'Not explicitly used as an argument by the theory.' },
    },
  },
  {
    criterion: 'Toponymic continuity',
    source: 'from antiquity to the present day',
    question: 'Is there unbroken use of the name since antiquity?',
    cells: {
      trad: { score: 'strong', note: 'Unbroken use of the name from antiquity to the present day — independent of philological interpretation.' },
      lefkada: { score: 'weak', note: 'The name "Leukas" was coined in the 7th c. BC by the Corinthians — the earlier name was "Nērikos".' },
      paliki: { score: 'weak', note: 'No continuity at all — Marinatos\u2019 "name migration" explanation is invoked instead.' },
    },
  },
  {
    criterion: 'Archaeology & geology',
    source: 'excavations 1900–today',
    question: 'Does field evidence support the identification?',
    cells: {
      trad: { score: 'medium', note: 'Mycenaean finds (Pelikata, "School of Homer"): habitation, not necessarily identification. No palace found.' },
      lefkada: { score: 'medium', note: 'Prehistoric cemeteries and walls at Nydri (2nd millennium BC) — accepted independently of the identification dispute.' },
      paliki: { score: 'weak', note: 'The critical geological prediction (a former sea channel) was falsified after 20 years of research.' },
    },
  },
  {
    criterion: 'Telemachus\u2019 route',
    source: 'Od. 15.295–300',
    question: 'Does the voyage from Pylos pass the Echinades / Acheloos before reaching "Ithaca"?',
    cells: {
      trad: { score: 'weak', note: 'No such route can be charted ending at present-day Ithaca.' },
      lefkada: { score: 'strong', note: 'Considered by the Lefkada advocates themselves their strongest argument — a passage along Acarnania.' },
      paliki: { score: 'na', note: 'Not developed as a support of the theory.' },
    },
  },
  {
    criterion: 'Communication with the mainland via a strait',
    source: 'Od. 4.671 · Strabo 10.2',
    question: 'Is the candidate linked to the mainland by a narrow strait/channel?',
    cells: {
      trad: { score: 'weak', note: 'Its strait faces Cephalonia (an island), not the mainland — the mainland lies far away.' },
      lefkada: { score: 'strong', note: 'The Lefkada channel opens directly opposite Acarnania — immediate communication with the mainland.' },
      paliki: { score: 'medium', note: 'A strait towards the main body of Cephalonia — internal, not towards the mainland.' },
    },
  },
  {
    criterion: 'Asteris & the suitors\u2019 ambush',
    source: 'Od. 4.844–847',
    question: 'Is there an ambush islet "midway between Ithaca and rugged Samos"?',
    cells: {
      trad: { score: 'medium', note: 'Daskaleio: the narrow Ithaca–Cephalonia strait, compatible with the ambush. Moderate to strong.' },
      lefkada: { score: 'weak', note: 'Identification with Arkoudi (Brewster) — contested, versus Daskaleio.' },
      paliki: { score: 'na', note: 'No stable Asteris identification is proposed.' },
    },
  },
  {
    criterion: '"very close to one another"',
    source: 'Od. 9.22',
    question: 'Are the islands of the group "very close to one another"?',
    cells: {
      trad: { score: 'medium', note: 'Satisfactory proximity, though not absolute adjacency.' },
      lefkada: { score: 'medium', note: 'Proximity to Acarnania; the group is rearranged.' },
      paliki: { score: 'strong', note: 'Solves the criterion by definition: Paliki–Same would be parts of the same island, very close indeed.' },
    },
  },
  {
    criterion: '"Kephallēnes"',
    source: 'Il. 2.631',
    question: 'Odysseus is presented as lord of the Kephallenians — where does that fit?',
    cells: {
      trad: { score: 'weak', note: 'The term is historically tied to Cephalonia, not to present-day Ithaca.' },
      lefkada: { score: 'medium', note: 'Strabo reads "held the mainland" as Kephallenian possession in Acarnania — indirect support.' },
      paliki: { score: 'strong', note: 'A direct link: Paliki is part of Cephalonia, the land of the Kephallenians.' },
    },
  },
];

export const NAMING_SYSTEMS = {
  names: ['Ἰθάκη', 'Σάμη', 'Δουλίχιο', 'Ζάκυνθος'],
  columns: [
    { key: 'trad', label: 'Traditional view' },
    { key: 'dorpfeld', label: 'Dörpfeld (1900)' },
    { key: 'paliki', label: 'Bittlestone / Diggle / Underhill (2005–2026)' },
  ],
  rows: {
    'Ἰθάκη': { trad: 'Present-day Ithaca', dorpfeld: 'Lefkada', paliki: 'Paliki (peninsula of Cephalonia)' },
    'Σάμη': { trad: 'Cephalonia', dorpfeld: 'Present-day Ithaca', paliki: 'The rest of Cephalonia' },
    'Δουλίχιο': { trad: 'An island of the Echinades (Dolicha/Makri)', dorpfeld: 'Cephalonia', paliki: 'Present-day Ithaca' },
    'Ζάκυνθος': { trad: 'Zakynthos — unchanged', dorpfeld: 'Zakynthos — unchanged', paliki: 'Zakynthos — unchanged' },
  },
  footnote: 'We observe that both rival theories agree on at least one point: Zakynthos remains fixed in every scenario — no one ever seriously disputed its identification. The entire uncertainty is concentrated exclusively in the other three names.',
};

export const MOVES = [
  { theory: 'Traditional view', move: 'Moves nothing.', result: 'The zophos/chthamalē riddle remains unsolved.' },
  { theory: 'Dörpfeld', move: 'Ithaca → Lefkada', result: '"Same" then had to move to present-day Ithaca.' },
  { theory: 'Dörpfeld (cont.)', move: 'Same → present-day Ithaca', result: 'Doulichion then had to move to Cephalonia — where, it is worth noting, the toponym "Doulicho" survives to this day.' },
  { theory: 'Bittlestone / Diggle–Underhill', move: 'Ithaca → Paliki', result: 'Doulichion then had to move to present-day Ithaca.' },
  { theory: 'Luce (1998)', move: 'Doulichion → Lefkada', result: 'It remains unclear what happens to the Lefkada–"mainland coast" relationship.' },
];

export const COORDS = [
  { island: 'Zakynthos', lat: '≈ 37.78° N', note: 'The southernmost of the group' },
  { island: 'Cephalonia (main mass)', lat: '≈ 38.21° N', note: 'Northern tip (Fiskardo): ≈ 38.47° N' },
  { island: 'Ithaca', lat: '≈ 38.37°–38.40° N', note: 'Northern tip: ≈ 38.47–38.48° N — marginally "first"' },
  { island: 'Lefkada', lat: '≈ 38.50°–38.83° N', note: 'The entire island lies further north — a dramatically clear advantage' },
];

export const ARGUMENTS = {
  trad: {
    title: 'Present-day Ithaca (traditional view)',
    strengths: [
      { text: 'Unbroken toponymic continuity — coins of the 4th–3rd c. BC bearing Odysseus\u2019 head, a recently discovered shrine with cult inscriptions (Agios Athanasios).', strength: 'Very strong, independent of philological interpretation' },
      { text: 'Mycenaean finds (Pelikata, "School of Homer").', strength: 'Moderate — shows habitation, not necessarily identification' },
      { text: 'The narrow strait with Cephalonia, compatible with the Asteris ambush.', strength: 'Moderate to strong' },
    ],
    weaknesses: [
      { text: '"Chthamalē" and "zophos = north" fit only marginally; Ithaca is neither clearly the northernmost nor the lowest of the group.' },
      { text: '"En dēmō(i) Ithakēs" structurally describes Ithaca as part of a larger entity — incompatible with a complete, self-contained island. A very strong, actively contradicted weakness.' },
    ],
  },
  lefkada: {
    title: 'Lefkada (Dörpfeld)',
    strengths: [
      { text: '"Zophos" = north (Strabo\u2019s reading): Lefkada is clearly, dramatically the northernmost of the whole group.', strength: 'Excellent match' },
      { text: '"Chthamalē" = "close to the mainland": a documented former peninsula of Acarnania.', strength: 'Excellent match' },
      { text: 'Radically solves the "nēsos" problem — it was a peninsula that later became an island.' },
      { text: 'A plausible etymological identification Nēriton = Nērikos, independent of disputed passages.' },
      { text: 'Telemachus\u2019 route towards the Echinades / Acheloos before "Ithaca".', strength: 'The Lefkada advocates\u2019 own strongest argument' },
    ],
    weaknesses: [
      { text: 'The Asteris–Arkoudi identification remains contested, against the traditional Asteris–Daskaleio which fits better the closer Ithaca–Cephalonia proximity.' },
    ],
  },
  paliki: {
    title: 'Paliki (Bittlestone / Diggle–Underhill)',
    strengths: [
      { text: 'The philological argument "avoidance of nēsos" + "dēmos of Ithaca".', strength: 'Strong, but equally generalisable to Lefkada' },
      { text: 'Direct link with the "Kephallenians" — Paliki is part of Cephalonia.' },
      { text: 'Solves "very close to one another": Paliki–Same would be by definition very close.' },
    ],
    weaknesses: [
      { text: 'The geological claim (a former island) remains contested after twenty years of research that did not confirm it.' },
      { text: 'No toponymic continuity, unlike present-day Ithaca.' },
      { text: 'Selective application of the philological argument — it is not explained why it does not apply equally to Lefkada.' },
    ],
  },
};

export const TIMELINE = [
  { year: '3rd c. BC', who: 'Eratosthenes', what: 'The sceptical quip: you will find where Odysseus wandered when you find the cobbler who stitched the bag of the winds.' },
  { year: '1st c. BC/AD', who: 'Strabo', what: 'Defence of Homer as a reliable geographer; the readings "chthamalē = close to the mainland" and "zophos = north" (10.2.12).' },
  { year: '1795', who: 'F. A. Wolf', what: 'Prolegomena ad Homerum — the foundation of the Homeric Question.' },
  { year: '1807', who: 'W. Gell', what: 'Places the Homeric capital at the Aetos isthmus of present-day Ithaca.' },
  { year: '1900', who: 'W. Dörpfeld', what: 'Announces at the German Institute in Athens: Lefkada is Homeric Ithaca. Excavations 1901–1913.' },
  { year: '1903–1905', who: 'Wilamowitz versus Dörpfeld', what: 'A caustic reply from the great philologist; Dörpfeld answers with Leukas (1905).' },
  { year: '1908', who: 'A. E. H. Goekoop', what: 'Proposes southern Cephalonia (Agios Georgios hill) as the site of Ithaca.' },
  { year: '1914 / 1926', who: 'A. Shewan', what: 'Extended critique of the Lefkada theory in the Journal of Hellenic Studies.' },
  { year: '1927', who: 'Dörpfeld', what: 'Alt-Ithaka, two volumes — the culmination of the Lefkada theory.' },
  { year: '1920s', who: 'F. Brewster', what: 'Lefkada as Homeric Same; Asteris as present-day Arkoudi.' },
  { year: '1998', who: 'J. V. Luce', what: 'Lefkada as Doulichion, not Ithaca — an argument from size and the number of suitors.' },
  { year: '2005', who: 'Bittlestone · Diggle · Underhill', what: 'Odysseus Unbound (Cambridge UP): Paliki as a former island = Homeric Ithaca.' },
  { year: '2026', who: 'Diggle & Underhill', what: '"In Search of Ithaca", Antigone Journal: the philological "rescue" — Ithaca never explicitly a "nēsos", but a "dēmos".' },
];

export const OVERVIEW_CARDS = [
  { id: 'prologue', title: 'The enigma', teaser: 'Ithaca is described as both "chthamale" (low-lying) and "farthest out", lying "towards zophos" — and present-day Ithaca probably does not match this description. Two thousand years of controversy, from Eratosthenes to 2026.' },
  { id: 'matrix', title: 'The 3 theories at a glance', teaser: 'Traditional view, Lefkada (Dörpfeld), Paliki (Bittlestone–Diggle–Underhill): twelve critical criteria, graded side by side — the core of the entire debate.' },
  { id: 'part1', title: 'The Homeric Question', teaser: 'Analysts versus Unitarians, the Catalogue of Ships as a separate document, and the disputed ending of the Odyssey — where the crucial toponyms happen to live.' },
  { id: 'ch4', title: 'The Lefkada theory', teaser: 'Wilhelm Dörpfeld, Schliemann\u2019s collaborator, moved Ithaca to Lefkada in 1900: zophos = north, chthamale = near the mainland, and Telemachus\u2019 voyage as proof.' },
  { id: 'ch5', title: 'The Paliki theory', teaser: 'Odysseus Unbound (2005) proposed the Paliki peninsula as a former island-Ithaca. Geology challenged the scenario — the philology of 2026 attempted the "rescue".' },
  { id: 'part3', title: 'The sources', teaser: 'Eight Homeric passages with original text and translation, and Strabo as an interpreter with a predetermined conclusion.' },
  { id: 'ch10', title: 'Was Homeric Ithaca an island?', teaser: 'In the entire Homeric corpus, Ithaca is never explicitly called a "nēsos" (island) — a silence that fuels both rival theories.' },
  { id: 'ch13', title: 'The transfer of the names', teaser: 'Every theory solves the problem by relocating it: the four-name puzzle (Ithaca–Same–Doulichion–Zakynthos) in two mutually opposed systems.' },
  { id: 'epilogue', title: 'What remains to be proven', teaser: 'No theory approaches certainty. Only an archaeological find that explicitly assigns the corresponding Homeric name to one of today\u2019s candidate islands will solve the riddle.' },
];

export const MAP_LABELS = [
  { old: 'I. St. Maura', modern: 'Lefkada', note: 'The northernmost island on the map — Dörpfeld\u2019s candidate, next to the Acarnanian coast.' },
  { old: 'I. Tiachi', modern: 'Ithaca', note: 'Present-day Ithaca, wedged between Lefkada and Cephalonia — neither the northernmost nor low-lying.' },
  { old: 'I. Cephalonia (Palari)', modern: 'Cephalonia & Paliki', note: 'The western Paliki peninsula is clearly distinguishable — the candidate of Bittlestone–Diggle–Underhill.' },
  { old: 'I. Zante', modern: 'Zakynthos', note: 'The only name no one ever disputed — fixed in every theory.' },
];

export const THEORY_TABS = [
  {
    key: 'trad',
    label: 'Traditional view',
    assign: { lefkada: 'Lefkada', ithaca: 'Ithaca', paliki: 'Paliki', cephMain: 'Same', zakynthos: 'Zakynthos', echinades: 'Doulichion' },
    summary: 'Ithaca: present-day Ithaca · Same: Cephalonia · Doulichion: the Echinades · Zakynthos: unchanged',
  },
  {
    key: 'dorpfeld',
    label: 'Dörpfeld (1900)',
    assign: { lefkada: 'Ithaca', ithaca: 'Same', paliki: 'Paliki', cephMain: 'Doulichion', zakynthos: 'Zakynthos', echinades: 'Echinades' },
    summary: 'Ithaca: Lefkada · Same: present-day Ithaca · Doulichion: Cephalonia · Zakynthos: unchanged',
  },
  {
    key: 'paliki',
    label: 'Bittlestone–Diggle–Underhill theory',
    assign: { lefkada: 'Lefkada', ithaca: 'Doulichion', paliki: 'Ithaca', cephMain: 'Same', zakynthos: 'Zakynthos', echinades: 'Echinades' },
    summary: 'Ithaca: Paliki · Same: the rest of Cephalonia · Doulichion: present-day Ithaca · Zakynthos: unchanged',
  },
];
